import {SectionItem} from 'common/components/items-menu/types';
import {BasicFunnelStep, DateKeyedFunnelSegments, FunnelStep, TrendChartType} from 'common/report/models/funnels/types';
import {LineChartValueType} from 'common/report/constants';
import {formatConciseDuration} from 'common/util/date';
import {IChartDataItem} from 'common/components/line-chart/types';
import {abbreviateNumber, commaizeNumber} from 'common/util';
import {formatPercent} from 'common/report/util/funnels/utils';

/** All trend chart view labels, labels used for display in the UI */
export const TrendTypeSelection: {[key in TrendChartType]: SectionItem} = {
  [TrendChartType.Conversion]: {
    label: `Conversion`,
  },
  [TrendChartType.TimeToConvert]: {
    label: `Time to convert`,
  },
  [TrendChartType.TopOfFunnel]: {
    label: `Top of funnel`,
  },
  [TrendChartType.BottomOfFunnel]: {
    label: `Bottom of funnel`,
  },
};

/**
 * A bundle of pure functions for extracting trend values for funnels based on TrendChartType.
 * The functions are pure, this-less, and safe to destructure
 */
export interface TrendValueGetter {
  getChartType: () => TrendChartType;
  getTrendValue: (step: FunnelStep | BasicFunnelStep, selectedStepIndex: null | number) => number;
  getStep: (steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number) => BasicFunnelStep;
  getValueType: () => LineChartValueType;
  formatValue: (value: number, axisLabelFormat?: boolean) => string;
}

const conversionValueGetter: TrendValueGetter = {
  getChartType(): TrendChartType {
    return TrendChartType.Conversion;
  },
  getTrendValue(step: FunnelStep | BasicFunnelStep, selectedStepIndex: null | number): number {
    let conversionRatio = 0;
    if (step) {
      conversionRatio = shouldShowOverallConversion(selectedStepIndex) ? step.overall_conv_ratio : step.step_conv_ratio;
    }
    return conversionRatio;
  },
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[steps.length - 1] : steps[selectedStepIndex + 1];
  },
  getValueType(): LineChartValueType {
    return LineChartValueType.Percent;
  },
  formatValue(value: number, _axisLabelFormat: boolean): string {
    return formatPercent(value, 2) as string;
  },
};

const timeToConvertValueGetter: TrendValueGetter = {
  getChartType(): TrendChartType {
    return TrendChartType.TimeToConvert;
  },
  getTrendValue(step: FunnelStep | BasicFunnelStep, selectedStepIndex: null | number): number {
    let averageTime = 0;
    if (shouldShowOverallConversion(selectedStepIndex)) {
      averageTime = step.avg_time_from_start ?? 0;
    } else {
      averageTime = step.avg_time ?? 0;
    }
    return averageTime;
  },
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[steps.length - 1] : steps[selectedStepIndex + 1];
  },
  getValueType(): LineChartValueType {
    return LineChartValueType.Duration;
  },
  formatValue(value: number, axisLabelFormat?: boolean): string {
    let precision = 2;
    if (axisLabelFormat) {
      precision = 1;
    }
    return formatConciseDuration(value, precision);
  },
};

const getFunnelTrendValue = (step: FunnelStep | BasicFunnelStep, _selectedStepIndex: null | number): number => {
  let count = 0;
  if (step) {
    count = step.count ? step.count : 0;
  }
  return count;
};

const topOfFunnelValueGetter: TrendValueGetter = {
  getChartType(): TrendChartType {
    return TrendChartType.TopOfFunnel;
  },
  getTrendValue: getFunnelTrendValue,
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[0] : steps[selectedStepIndex];
  },
  getValueType(): LineChartValueType {
    return LineChartValueType.Simple;
  },
  formatValue(value: number, axisLabelFormat?: boolean): string {
    return formatSimpleValueType(value, axisLabelFormat);
  },
};

const bottomOfFunnelValueGetter: TrendValueGetter = {
  getChartType(): TrendChartType {
    return TrendChartType.BottomOfFunnel;
  },
  getTrendValue: getFunnelTrendValue,
  getStep(steps: Array<FunnelStep | BasicFunnelStep>, selectedStepIndex: null | number): BasicFunnelStep {
    return shouldShowOverallConversion(selectedStepIndex) ? steps[steps.length - 1] : steps[selectedStepIndex + 1];
  },
  getValueType(): LineChartValueType {
    return LineChartValueType.Simple;
  },
  formatValue(value: number, axisLabelFormat?: boolean): string {
    return formatSimpleValueType(value, axisLabelFormat);
  },
};

export function shouldShowOverallConversion(eventIndex: number | null): boolean {
  return !Number.isInteger(eventIndex);
}

function formatSimpleValueType(value: number, axisLabelFormat?: boolean): string {
  if (axisLabelFormat) {
    return abbreviateNumber(value, 1);
  } else {
    // commaize if not a float
    return Number.isInteger(value) ? commaizeNumber(value) : value.toString();
  }
}

/**
 * Returns the TrendValueGetter object corresponding to the TrendChartType that was passed in.
 * The TrendValueGetter object is used to return the appropriate value,
 * within the array of funnel steps provided, as well as the appropriate value representation
 * for each TrendChartType.
 *
 * @param  trendChartType  the trend chart (as a TrendChartType) for which to return its TrendValueGetter
 * @return the TrendValueGetter for the trendChartType specified
 */
export function getFunnelTrendValueGetter(trendChartType: TrendChartType | undefined): TrendValueGetter {
  const valueGetter: {[key in TrendChartType]: TrendValueGetter} = {
    [TrendChartType.Conversion]: conversionValueGetter,
    [TrendChartType.TimeToConvert]: timeToConvertValueGetter,
    [TrendChartType.TopOfFunnel]: topOfFunnelValueGetter,
    [TrendChartType.BottomOfFunnel]: bottomOfFunnelValueGetter,
  };
  return valueGetter[trendChartType];
}

/**
 * Given several trend chart parameters, returns the chart data for a line chart
 * in a format understood by the mp-line-chart common component with params to format and style the
 * series on the line chart.
 * @param eventIndex index of the event/steps chosen for the chart/report
 * @param selectedSeriesColors a map of the segments selected to appear in the chart mapped as segment prop to segment color
 * @param orderedTrends an array of DateKeyedFunnelSegments, all the possible segments that could appear on the chart
 * @param trendTypeSelection a TrendChartType, the trend view selected for the chart
 * @param dashStyle the line style used when displaying the selected data on the chart
 */
export function getTrendChartData({
  eventIndex,
  selectedSeriesColors,
  orderedTrends,
  trendTypeSelection,
  dashStyle,
}: {
  eventIndex: null | number;
  selectedSeriesColors: Map<string, string>;
  orderedTrends: DateKeyedFunnelSegments[];
  trendTypeSelection: TrendChartType;
  dashStyle?: string;
}): IChartDataItem[] {
  return orderedTrends[0].segments.reduce((selectedSegments, funnelSegment, segmentIndex) => {
    if (!selectedSeriesColors.has(funnelSegment.prop)) {
      return selectedSegments;
    }
    const segmentData = orderedTrends.map(({date, segments}) => {
      const timestamp = date.getTime();
      const steps = segments[segmentIndex].steps;
      const trendValueGetter = getFunnelTrendValueGetter(trendTypeSelection);
      return [timestamp, trendValueGetter.getTrendValue(trendValueGetter.getStep(steps, eventIndex), eventIndex)];
    });
    selectedSegments.push({
      color: selectedSeriesColors.get(funnelSegment.prop),
      name: funnelSegment.propName,
      label: funnelSegment.prop,
      data: segmentData,
      dashStyle,
      visible: true,
    });
    return selectedSegments;
  }, []);
}
